import { Component, createSignal, JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import { LINE_STYLES } from '../utils/layer-default-values';
import { LineWeightSelector } from '../line-weight-selector/LineWeightSelector';
import { LineStyleSelector } from '../line-style-selector/LineStyleSelector';
import ColorProperty from '../color-property/ColorProperty';
import { BtnClasses, IconButton } from '@tcweb/connect-ui';

export interface LayerCreateProps {
  onLayerCreate(param: LayerCreateParams): void;
  onClose(): void;
}

export interface LayerCreateParams {
  name: string;
  style: string;
  thickness: number;
  color: string;
}

export const LayerCreatePopup: Component<LayerCreateProps> = (props) => {
  const DEFAULT_COLOR: string = '#0063a3';

  const [name, setName] = createSignal<string>('');
  const [lineThickness, setLineThickness] = createSignal<number>(1);
  const [lineStyle, setLineStyle] = createSignal<string>(LINE_STYLES[0].value);
  const [lineColor, setLineColor] = createSignal<string>(DEFAULT_COLOR);

  return (
    <div data-testid="layer-create-popup" class="w-full flex flex-col gap-2">
      <div class="flex justify-between">
        <h2>{t('general.createLayer')}</h2>
        <div class="text-right">
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="close"
            size="icon-medium"
            clicked={() => props.onClose()}
          />
        </div>
      </div>

      {/* Layer Name */}
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-6">{t('general.name')}</label>
          <div class="border border-gray-2 rounded-md bg-white flex h-[42px] items-center">
            <input
              class="w-full h-full px-3.5"
              placeholder={t('shared.data-prep.enter-layer-name')}
              on:input={(e: Event) =>
                setName((e.target as HTMLInputElement).value)
              }
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-6">{t('general.linecolor')}</label>
          <div class="border border-gray-2 rounded-md bg-white flex h-[42px] items-center px-3.5">
            <ColorProperty
              selectedColor={lineColor()}
              objectColor={lineColor()}
              isByLayer={false}
              canEdit={true}
              disabled={false}
              cssAlignment="right-[1px]"
              onColorChange={(e) => {
                if (e) {
                  setLineColor(e);
                }
              }}
              noBorder={true}
              hideIcon={true}
            />
          </div>
        </div>
      </div>

      {/* Line Weight and Line Style */}
      <div class="flex flex-row gap-2">
        <LineWeightSelector
          initialLineThickness={lineThickness()}
          onLineThicknessChange={setLineThickness}
        />
        <LineStyleSelector
          initialLineStyle={lineStyle()}
          onLineStyleChange={setLineStyle}
        />
      </div>

      {/* Preview */}
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-6">{t('general.preview')}</label>
          <div class="h-[42px] border border-solid flex justify-center items-center">
            <div
              class="w-[60%]"
              style={{
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'border-top-color': lineColor(),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'border-top-width': `${lineThickness()}px`,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'border-top-style':
                  lineStyle() as JSX.CSSProperties['border-top-style'],
              }}
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div class="flex flex-col gap-3">
        <div class="flex flex-row gap-1 justify-end">
          <modus-wc-button
            aria-label="Cancel"
            color="tertiary"
            shape="rectangle"
            size="md"
            type="button"
            variant="borderless"
            attr:full-width={false}
            onClick={() => {
              props.onClose();
            }}
          >
            {t('general.cancel')}
          </modus-wc-button>
          <modus-wc-button
            aria-label="Create"
            color="primary"
            shape="rectangle"
            size="md"
            type="button"
            variant="filled"
            attr:full-width={false}
            disabled={name().trim().length === 0}
            onClick={() => {
              props.onLayerCreate({
                color: lineColor(),
                name: name().trim(),
                style: lineStyle(),
                thickness: lineThickness(),
              });
              props.onClose();
            }}
          >
            {t('general.create')}
          </modus-wc-button>
        </div>
      </div>
    </div>
  );
};
